import { LocaleLink } from '@/components/LocaleLink'

import { cn } from '@/utilities/ui'

import { CategoryLabel, ReadTime, externalArticleLink } from './common'
import { HomeCardImage } from './HomeCardImage'
import {
  HOME_BI_NEWS_IMAGE_HOVER,
  HOME_BI_NEWS_ITEM_GROUP,
  HOME_BI_NEWS_TITLE_LINK,
} from './home-bi-layout'
import type { HomeBiNewsStoryRowItem } from './types'

/** home-middle, hero secundário — máximo 100×75px em todos os breakpoints. */
const COMPACT_THUMB =
  'relative h-[75px] w-[100px] shrink-0 overflow-hidden rounded-[var(--post-thumb-radius,0px)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'

/** home-footer: mobile = meio (75×100); desktop = editorial (174×232) */
const FOOTER_THUMB =
  'relative h-[75px] w-[100px] shrink-0 overflow-hidden rounded-[var(--post-thumb-radius,0px)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none lg:h-[174px] lg:w-[232px]'

/**
 * Linha título | imagem com hover unificado.
 * `compact` — home-middle / hero secundário.
 * `editorial` — home-footer (thumb grande só em `lg+`; mobile igual ao meio).
 */
export function HomeBiNewsStoryRow({
  item,
  index,
  variant = 'compact',
  showThumbnail = true,
}: {
  item: HomeBiNewsStoryRowItem
  index: number
  variant?: 'compact' | 'editorial'
  showThumbnail?: boolean
}) {
  const isEditorial = variant === 'editorial'
  const categoryLabel = item.category ?? item.badge

  return (
    <article
      className={cn(
        'bg-card text-card-foreground flex w-full min-w-0 flex-col gap-0 rounded-none py-0 shadow-none',
        HOME_BI_NEWS_ITEM_GROUP,
        index === 0
          ? 'border-0'
          : isEditorial
            ? 'border-border mt-4 border-x-0 border-b-0 border-t pt-4'
            : 'border-border mt-3 border-x-0 border-b-0 border-t pt-3',
      )}
    >
      <div
        className={cn(
          'flex w-full',
          isEditorial
            ? 'items-center gap-3 lg:items-start lg:gap-5'
            : 'items-center gap-3 lg:gap-4',
        )}
      >
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col gap-1 p-0 pr-0',
            isEditorial && 'justify-center gap-1.5 py-0.5',
          )}
        >
          {categoryLabel ? (
            <CategoryLabel href={item.categoryHref}>{categoryLabel}</CategoryLabel>
          ) : null}
          <h3
            className={cn(
              'text-sm leading-snug font-semibold',
              isEditorial ? 'md:text-base lg:text-[17px]' : 'md:text-[15px] lg:text-base',
            )}
          >
            <LocaleLink
              {...externalArticleLink}
              href={item.href}
              className={HOME_BI_NEWS_TITLE_LINK}
            >
              {item.title}
            </LocaleLink>
          </h3>
          {isEditorial && item.description ? (
            <p className="text-muted-foreground line-clamp-2 text-sm leading-relaxed md:line-clamp-3">
              {item.description}
            </p>
          ) : null}
          {item.read ? <ReadTime>{item.read}</ReadTime> : null}
        </div>
        {showThumbnail ? (
          <LocaleLink
            {...externalArticleLink}
            href={item.href}
            className={isEditorial ? FOOTER_THUMB : COMPACT_THUMB}
            tabIndex={-1}
            aria-hidden
          >
            <HomeCardImage
              src={item.img}
              srcSizes={item.imgSizes}
              alt={item.alt}
              className={HOME_BI_NEWS_IMAGE_HOVER}
              sizes={isEditorial ? '(min-width: 1024px) 232px, 100px' : '100px'}
            />
          </LocaleLink>
        ) : null}
      </div>
    </article>
  )
}
